/**
 * Drawing sheet layout — pure geometry → a renderable sheet model.
 *
 * Takes HLR view projections (2D segments in view coords, Y up) and
 * produces everything a renderer needs in SHEET millimetres (Y up; SVG/PDF
 * renderers flip as needed): styled segments, dimension annotations, section
 * hatching, and the title block. Third-angle arrangement: front center,
 * top ABOVE it, right view to the RIGHT, iso in the corner.
 */

import { chainIntoLoops } from '../geometry/sketch';
import type { Vec2 } from '../timeline/types';

export interface ViewGeometry {
  visible: Float32Array;
  hidden: Float32Array;
  circles: Float32Array;
  sectionSegs: Float32Array;
}

export interface SheetText {
  p: [number, number];
  text: string;
  size: number;
  anchor: 'start' | 'middle' | 'end';
}

export interface SheetModel {
  /** Sheet size in mm. */
  width: number;
  height: number;
  /** Styled segment buckets, flat [x1,y1,x2,y2]* in sheet mm (Y up). */
  visible: number[];
  hidden: number[];
  section: number[];
  hatch: number[];
  dims: number[];
  frame: number[];
  texts: SheetText[];
  scale: number;
  /** Placed view regions (sheet mm) — custom-dimension picks map through
   * these back to TRUE model distances. */
  views: { name: string; x0: number; y0: number; x1: number; y1: number; scale: number }[];
}

export interface SheetOptions {
  size?: 'A3' | 'A4';
  /** Explicit model→sheet scale for the ortho cluster; omit = auto. */
  scale?: number;
}

const SHEET_SIZES = { A3: [420, 297], A4: [297, 210] } as const;
const MARGIN = 12;
const GAP = 18;
const STD_SCALES = [10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05, 0.02];

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function boundsOf(view: ViewGeometry): Bounds {
  const b: Bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  for (const arr of [view.visible, view.hidden, view.sectionSegs]) {
    for (let i = 0; i < arr.length; i += 2) {
      b.minX = Math.min(b.minX, arr[i]!);
      b.maxX = Math.max(b.maxX, arr[i]!);
      b.minY = Math.min(b.minY, arr[i + 1]!);
      b.maxY = Math.max(b.maxY, arr[i + 1]!);
    }
  }
  if (!Number.isFinite(b.minX)) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  return b;
}

const W = (b: Bounds): number => b.maxX - b.minX;
const H = (b: Bounds): number => b.maxY - b.minY;

/** Chained loops from flat segments (for hatching). */
function segmentsToLoops(segs: Float32Array): Vec2[][] {
  const polylines: Vec2[][] = [];
  for (let i = 0; i + 3 < segs.length; i += 4) {
    polylines.push([
      [segs[i]!, segs[i + 1]!],
      [segs[i + 2]!, segs[i + 3]!],
    ]);
  }
  return chainIntoLoops(polylines).loops;
}

/** 45° hatch lines clipped to even-odd loops. Coords in the loops' space. */
export function hatchLoops(loops: readonly Vec2[][], spacing: number): number[] {
  if (loops.length === 0) return [];
  const S = Math.SQRT1_2;
  // Rotate −45° so hatch lines become horizontal scanlines.
  const rot = (p: Vec2): Vec2 => [p[0] * S + p[1] * S, -p[0] * S + p[1] * S];
  const unrot = (x: number, y: number): [number, number] => [x * S - y * S, x * S + y * S];
  const rl = loops.map((loop) => loop.map(rot));
  let minY = Infinity, maxY = -Infinity;
  for (const loop of rl) for (const p of loop) {
    minY = Math.min(minY, p[1]);
    maxY = Math.max(maxY, p[1]);
  }
  const out: number[] = [];
  for (let y = minY + spacing / 2; y < maxY; y += spacing) {
    // All crossings of the scanline against every loop edge (even-odd).
    const xs: number[] = [];
    for (const loop of rl) {
      for (let i = 0; i < loop.length; i++) {
        const a = loop[i]!;
        const b = loop[(i + 1) % loop.length]!;
        if (a[1] === b[1]) continue;
        if (y >= Math.min(a[1], b[1]) && y < Math.max(a[1], b[1])) {
          xs.push(a[0] + ((y - a[1]) / (b[1] - a[1])) * (b[0] - a[0]));
        }
      }
    }
    xs.sort((p, q) => p - q);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const [x1, y1] = unrot(xs[i]!, y);
      const [x2, y2] = unrot(xs[i + 1]!, y);
      out.push(x1, y1, x2, y2);
    }
  }
  return out;
}

/** Arrowhead segments for a dimension line end pointing along (dx,dy). */
function arrow(out: number[], x: number, y: number, dx: number, dy: number): void {
  const L = 2.4, Wd = 0.8;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len, uy = dy / len;
  out.push(x, y, x - ux * L - uy * Wd, y - uy * L + ux * Wd);
  out.push(x, y, x - ux * L + uy * Wd, y - uy * L - ux * Wd);
}

const fmt = (v: number): string => String(Math.round(v * 100) / 100);

/** One placed orthographic view + its auto-dimensions. */
function placeView(
  model: SheetModel,
  view: ViewGeometry,
  b: Bounds,
  scale: number,
  ox: number,
  oy: number,
  label: string,
  dims: 'full' | 'holes' | 'none',
): void {
  const tx = (x: number): number => ox + (x - b.minX) * scale;
  const ty = (y: number): number => oy + (y - b.minY) * scale;
  const emit = (src: Float32Array, dst: number[]): void => {
    for (let i = 0; i + 3 < src.length; i += 4) {
      dst.push(tx(src[i]!), ty(src[i + 1]!), tx(src[i + 2]!), ty(src[i + 3]!));
    }
  };
  emit(view.visible, model.visible);
  emit(view.hidden, model.hidden);
  emit(view.sectionSegs, model.section);
  model.views.push({
    name: label,
    x0: tx(b.minX),
    y0: ty(b.minY),
    x1: tx(b.maxX),
    y1: ty(b.maxY),
    scale,
  });
  if (view.sectionSegs.length > 0) {
    const loops = segmentsToLoops(view.sectionSegs);
    const hatch = hatchLoops(loops, 3 / scale);
    for (let i = 0; i + 3 < hatch.length; i += 4) {
      model.hatch.push(tx(hatch[i]!), ty(hatch[i + 1]!), tx(hatch[i + 2]!), ty(hatch[i + 3]!));
    }
  }

  model.texts.push({
    // Fully-dimensioned views carry a width dim below — drop the label
    // beneath it instead of colliding.
    p: [ox + (W(b) * scale) / 2, oy - (dims === 'full' ? 16 : 7)],
    text: label,
    size: 3.5,
    anchor: 'middle',
  });

  if (dims === 'none') return;
  const x0 = tx(b.minX), x1 = tx(b.maxX), y0 = ty(b.minY), y1 = ty(b.maxY);
  if (dims === 'full') {

  // Overall width: dimension line below the view.
  const dyW = y0 - 9;
  model.dims.push(x0, y0 - 2, x0, dyW - 2, x1, y0 - 2, x1, dyW - 2); // extension lines
  model.dims.push(x0, dyW, x1, dyW);
  arrow(model.dims, x0, dyW, -1, 0);
  arrow(model.dims, x1, dyW, 1, 0);
  model.texts.push({ p: [(x0 + x1) / 2, dyW + 1.2], text: fmt(W(b)), size: 3, anchor: 'middle' });

  // Overall height: dimension line left of the view.
  const dxH = x0 - 9;
  model.dims.push(x0 - 2, y0, dxH - 2, y0, x0 - 2, y1, dxH - 2, y1);
  model.dims.push(dxH, y0, dxH, y1);
  arrow(model.dims, dxH, y0, 0, -1);
  arrow(model.dims, dxH, y1, 0, 1);
  model.texts.push({ p: [dxH - 1.5, (y0 + y1) / 2], text: fmt(H(b)), size: 3, anchor: 'end' });
  }

  // Hole diameters: one leader per UNIQUE radius (largest circles first).
  const seen = new Set<string>();
  const circles: { x: number; y: number; r: number }[] = [];
  for (let i = 0; i + 2 < view.circles.length; i += 3) {
    circles.push({ x: view.circles[i]!, y: view.circles[i + 1]!, r: view.circles[i + 2]! });
  }
  circles.sort((a, c) => c.r - a.r);
  let placed = 0;
  for (const c of circles) {
    const key = fmt(c.r * 2);
    if (seen.has(key) || placed >= 3) continue;
    seen.add(key);
    placed++;
    const cx = tx(c.x), cy = ty(c.y);
    const ex = cx + c.r * scale * 0.7071 + 6;
    const ey = cy + c.r * scale * 0.7071 + 6;
    model.dims.push(cx + c.r * scale * 0.7071, cy + c.r * scale * 0.7071, ex, ey, ex, ey, ex + 5, ey);
    model.texts.push({ p: [ex + 6, ey + 1], text: `⌀${key}`, size: 3, anchor: 'start' });
  }
}

/**
 * User-placed aligned dimension between two SHEET points: extension lines,
 * offset dimension line with arrows, and the (model-space) value text.
 */
export function appendAlignedDim(
  model: SheetModel,
  a: [number, number],
  b: [number, number],
  text: string,
): void {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len < 0.5) return;
  const ux = dx / len, uy = dy / len;
  // Offset side: away from the sheet center, so dims tend outward.
  let nx = -uy, ny = ux;
  const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
  if ((mx - model.width / 2) * nx + (my - model.height / 2) * ny < 0) {
    nx = -nx; ny = -ny;
  }
  const off = 8;
  const A: [number, number] = [a[0] + nx * off, a[1] + ny * off];
  const B: [number, number] = [b[0] + nx * off, b[1] + ny * off];
  model.dims.push(a[0] + nx * 1.5, a[1] + ny * 1.5, A[0] + nx * 2, A[1] + ny * 2);
  model.dims.push(b[0] + nx * 1.5, b[1] + ny * 1.5, B[0] + nx * 2, B[1] + ny * 2);
  model.dims.push(A[0], A[1], B[0], B[1]);
  arrow(model.dims, A[0], A[1], -ux, -uy);
  arrow(model.dims, B[0], B[1], ux, uy);
  model.texts.push({
    p: [(A[0] + B[0]) / 2 + nx * 2.2, (A[1] + B[1]) / 2 + ny * 2.2],
    text,
    size: 3,
    anchor: 'middle',
  });
}

/** The view region containing a sheet point (custom-dim value mapping). */
export function viewAt(
  model: SheetModel,
  p: [number, number],
  slack = 12,
): SheetModel['views'][number] | null {
  let best: SheetModel['views'][number] | null = null;
  for (const v of model.views) {
    if (p[0] >= v.x0 - slack && p[0] <= v.x1 + slack && p[1] >= v.y0 - slack && p[1] <= v.y1 + slack) {
      if (!best || v.scale > best.scale) best = v; // prefer the tighter view
    }
  }
  return best;
}

export interface SheetInput {
  front: ViewGeometry;
  top: ViewGeometry;
  right: ViewGeometry;
  iso: ViewGeometry;
  section?: { geo: ViewGeometry; label: string };
  projectName: string;
  dateText: string;
}

/** Compose the full sheet: views, dims, frame, title block. */
export function layoutSheet(input: SheetInput, options: SheetOptions = {}): SheetModel {
  const [SHEET_W, SHEET_H] = SHEET_SIZES[options.size ?? 'A3'];
  const model: SheetModel = {
    width: SHEET_W,
    height: SHEET_H,
    visible: [], hidden: [], section: [], hatch: [], dims: [], frame: [],
    texts: [],
    scale: 1,
    views: [],
  };

  const bF = boundsOf(input.front);
  const bT = boundsOf(input.top);
  const bR = boundsOf(input.right);
  const bI = boundsOf(input.iso);
  const bS = input.section ? boundsOf(input.section.geo) : null;

  // Space budget: ortho cluster occupies the left ~2/3; iso + section the
  // right column. Pick the largest standard scale that fits the cluster.
  const dimPad = 16; // dims + labels around each view
  const clusterW = SHEET_W * 0.6 - 2 * MARGIN;
  const clusterH = SHEET_H - 2 * MARGIN - 18; // title strip
  const needW = W(bF) + W(bR);
  const needH = H(bF) + H(bT);
  const scale =
    options.scale ??
    STD_SCALES.find(
      (s) => needW * s + GAP + 3 * dimPad <= clusterW && needH * s + GAP + 3 * dimPad <= clusterH,
    ) ?? 0.01;
  model.scale = scale;

  // Third-angle: front bottom-left of the cluster, top above it, right beside.
  const fx = MARGIN + dimPad + 8;
  const fy = MARGIN + 20 + dimPad;
  placeView(model, input.front, bF, scale, fx, fy, 'FRONT', 'full');
  placeView(
    model, input.top, bT, scale,
    fx, fy + H(bF) * scale + GAP + dimPad, 'TOP', 'holes',
  );
  placeView(
    model, input.right, bR, scale,
    fx + W(bF) * scale + GAP + dimPad, fy, 'RIGHT', 'holes',
  );

  // Right column: iso (top), section (below) — independent fitted scales.
  const colX = SHEET_W * 0.62;
  const colW = SHEET_W - colX - MARGIN;
  const isoScale = Math.min(colW / Math.max(W(bI), 1), (SHEET_H * 0.38) / Math.max(H(bI), 1)) * 0.9;
  placeView(
    model, input.iso, bI, isoScale,
    colX + (colW - W(bI) * isoScale) / 2, SHEET_H * 0.55, 'ISO', 'none',
  );
  if (input.section && bS) {
    const secScale = Math.min(colW / Math.max(W(bS), 1), (SHEET_H * 0.3) / Math.max(H(bS), 1)) * 0.9;
    placeView(
      model, input.section.geo, bS, secScale,
      colX + (colW - W(bS) * secScale) / 2, MARGIN + 40, input.section.label, 'none',
    );
  }

  // Frame + title block (bottom-right).
  const f = (x1: number, y1: number, x2: number, y2: number): void => {
    model.frame.push(x1, y1, x2, y2);
  };
  f(MARGIN, MARGIN, SHEET_W - MARGIN, MARGIN);
  f(SHEET_W - MARGIN, MARGIN, SHEET_W - MARGIN, SHEET_H - MARGIN);
  f(SHEET_W - MARGIN, SHEET_H - MARGIN, MARGIN, SHEET_H - MARGIN);
  f(MARGIN, SHEET_H - MARGIN, MARGIN, MARGIN);
  const tbW = 150, tbH = 26;
  const tbX = SHEET_W - MARGIN - tbW, tbY = MARGIN;
  f(tbX, tbY, tbX + tbW, tbY);
  f(tbX, tbY + tbH, tbX + tbW, tbY + tbH);
  f(tbX, tbY, tbX, tbY + tbH);
  f(tbX + tbW * 0.55, tbY, tbX + tbW * 0.55, tbY + tbH);
  f(tbX, tbY + tbH / 2, tbX + tbW, tbY + tbH / 2);
  model.texts.push(
    { p: [tbX + 4, tbY + tbH - 8], text: input.projectName, size: 4, anchor: 'start' },
    { p: [tbX + 4, tbY + 4], text: 'NovaCAD · mm', size: 3, anchor: 'start' },
    { p: [tbX + tbW * 0.55 + 4, tbY + tbH - 8], text: scale >= 1 ? `SCALE ${fmt(scale)}:1` : `SCALE 1:${fmt(1 / scale)}`, size: 3, anchor: 'start' },
    { p: [tbX + tbW * 0.55 + 4, tbY + 4], text: input.dateText, size: 3, anchor: 'start' },
  );
  return model;
}
